import { useState, useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

const useCadmObject = (objType, props) => {
    const navigate = useNavigate()
    const cacheMinutes = 60
    const localStorageKey = 'cadm_objects'
    const localStorageData = localStorage.getItem(localStorageKey) ? JSON.parse(localStorage.getItem(localStorageKey)) : {}
    const [lastUpdated, setLastUpdated] = useState(localStorageData?.lastUpdated || 0)
    const [cadmObjects, setCadmObjects] = useState(localStorageData?.cadmObjects || [])

    useEffect(() => {
        if( lastUpdated + (1000 * 60 * cacheMinutes) > Date.now() ) {
            return
        }
        reloadCadmObjects()
    }, [])

    const reloadCadmObjects = () => {
        setLastUpdated(Date.now())
        fetch('/v0/cadm_objects', { credentials: 'same-origin' })
            .then(function(response) {
                if( response.status === 403 ) {
                    // They're logged out server-side; make sure that's true on client too
                    //dispatch(logout())
                    throw new Error(response.statusText)
                }
                if (!response.ok) throw response
                return response.json()
            })
            .then(function(json) {
                setCadmObjects(json.results)
                localStorage.setItem(localStorageKey, JSON.stringify({
                    lastUpdated: Date.now(),
                    cadmObjects: json.results
                }))
            })
            .catch(error => console.log("useCadmObject.reload error", error))
        }

    const pscState = useSelector(state => state.pscState.value)
    const project = pscState.project

    const stdDocumentMap = useMemo(() => {
        if (typeof(project) === 'undefined' || typeof(project.cadm_object_map) === 'undefined') {
            return
        }
        const stdMap = project?.cadm_object_map.find(function (d) { return d.cadm_object_href === objType } )
        return stdMap?.std_document_map
    }, [project, objType])

    const referenceStructure = useMemo(() => {
        if (typeof(project) === 'undefined' || typeof(project.cadm_object_map) === 'undefined') {
            return
        }
        const structures = project?.project_configuration_scheme.reference_structures
        return structures ? structures.find(function (d) { return d.cadm_object.href === objType })?.structure : null
    }, [project, objType])

    const initCustomFields = useMemo(() => {
        const cadm = cadmObjects?.find(function (i) { return i.href === objType })
        return cadm?.custom_fields.map(function (i) {
            return {
                ...i,
                cadm_object_custom_field_id: i.id,
                text_value: ''
            }
        })
    }, [cadmObjects, objType])

    const getStdDocument = (id) => {
        let obj = {}
        stdDocumentMap?.forEach(function (item) {
            if (item.std_document_id === id) {
                // Another horribly mix-up of things here. This list doesn't have name ! It has std_document_name and label.
                obj = { ...item }
                obj.name = item.std_document_name
            }
        })
        return obj
    }

    const getUnarchivedStdDocuments = () => {
        return stdDocumentMap.filter((item) => item.is_archived === false).map((item) => ({
            ...item,
            extraText: item.template_id && item.template_id > 0 ? "SMART Template" : undefined
        }))
    }
    
    return {
        cadmObjects,
        referenceStructure,
        stdDocumentMap,
        initCustomFields,
        getStdDocument,
        getUnarchivedStdDocuments,
        reloadCadmObjects,
    }
}

export default useCadmObject
